export type Me = { id: number; username: string; is_admin: boolean };
export type Conversation = {
  id: number;
  title: string;
  claude_session_id: string | null;
  created_at: string;
  updated_at: string;
};
export type Message = {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
};
export type AdminUser = { id: number; username: string; is_admin: number; created_at: string };
export type Environment = {
  odoo: {
    url: string | null; db: string | null; login: string | null;
    version: string | null; reachable: boolean; error: string | null;
    storableField: 'is_storable' | 'detailed_type' | null;
    hasPartnerMobile: boolean | null;
    toValidateActivityTypeId: number;
    pdfPasswordConfigured: boolean;
  };
  n8n: {
    domain: string | null; apiBase: string | null;
    apiKeyConfigured: boolean; apiKeyExpiresAt: string | null; apiKeyExpired: boolean;
    reachable: boolean; workflowsTotal: number | null; workflowsActive: number | null;
    error: string | null;
  };
  app: { nodeEnv: string };
};
export type ConversationsPage = { items: Conversation[]; total: number; limit: number; offset: number };

/**
 * Toutes les listes d'écran sont paginées côté serveur : `total` est le nombre de
 * lignes du jeu FILTRÉ (pas de la page), `items` la tranche demandée. Les écrans
 * dont les filtres portent sur un champ calculé par Odoo (Suivi, Achats, Stocks,
 * Traçabilité) renvoient en plus `counts`, calculés sur l'ensemble lu — ce sont
 * les libellés des filtres, ils ne doivent pas dépendre de la page affichée.
 */
export type Page<T> = { items: T[]; total: number; limit: number; offset: number };
export type CountedPage<T, C> = Page<T> & { counts: C };
export type PageOpts = { limit?: number; offset?: number };

export type OrderDeliveryFilter = 'tout' | 'pending' | 'started' | 'partial' | 'full';
export type OrderInvoiceFilter = 'tout' | 'to invoice' | 'invoiced' | 'no';
export type InvoiceStateFilter = 'tout' | 'draft' | 'posted';
export type InvoicePayFilter = 'tout' | 'paid' | 'unpaid' | 'overdue';
export type PurchasePayFilter = 'tout' | 'paid' | 'unpaid';

/** Tri et recherche des écrans de liste (ticket #22) — toujours résolus côté serveur. */
export type SortOpts = { sort?: string; dir?: 'asc' | 'desc'; q?: string };
/** Sérialise tri + recherche ; `dir` n'est envoyé qu'avec une colonne de tri. */
function sortParams(o?: SortOpts): Record<string, string | undefined> {
  return { sort: o?.sort, dir: o?.sort ? o?.dir : undefined, q: o?.q || undefined };
}

/** Sérialise limit/offset (+ paires supplémentaires) en query string. */
function pageParams(opts?: PageOpts, extra?: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  if (opts?.limit != null) p.set('limit', String(opts.limit));
  if (opts?.offset != null) p.set('offset', String(opts.offset));
  for (const [k, v] of Object.entries(extra ?? {})) if (v) p.set(k, v);
  const qs = p.toString();
  return qs ? '?' + qs : '';
}

function rangeParams(r?: DashRange): string {
  return r ? `?from=${r.from}&to=${r.to}` : '';
}

/** Origine d'une vente : transcrite d'un email par le pipeline n8n, ou saisie à la main. */
export type SaleSource = 'agent' | 'manuel';

/** Un email parti pour ce document, tel qu'Odoo l'a enregistré (ticket #39). */
export type SentEmail = {
  messageId: number;
  date: string;
  subject: string | null;
  to: string[];
  status: 'sent' | 'failed' | 'pending';
  failure: string | null;
  attachments: number;
};

export type PendingQuote = {
  id: number;
  name: string;
  state: string;
  source: SaleSource;
  amount_total: number;
  currency: string | null;
  partner: string | null;
  date_order: string | false;
  created_at?: string;
};
export type QuoteLine = {
  id: number;
  name: string;
  qty: number;
  price_unit: number;
  subtotal: number;
  product: string | null;
  /** Fiche produit visée par le lien vers l'écran Stocks (null = ligne libre). */
  product_id: number | null;
};

/**
 * « Ce mouvement vient de ce document » : origine d'un bon de transfert résolue
 * côté serveur vers l'écran qui porte le document (cf. backend/src/docLinks.ts).
 */
export type DocLink = { kind: 'vente' | 'achat'; id: number; name: string };

/** URL interne d'un document lié, pour un <Link>. */
export function docHref(l: DocLink): string {
  return l.kind === 'vente' ? `/ventes?vente=${l.id}` : `/achats?facture=${l.id}`;
}
export type QuoteAddress = { id: number; name: string; lines: string[] };
export type QuoteDetail = {
  id: number;
  name: string;
  state: string;
  source: SaleSource;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  invoice_address: QuoteAddress | null;
  shipping_address: QuoteAddress | null;
  same_address: boolean;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  currency: string | null;
  date_order: string | false;
  created_at?: string;
  client_order_ref: string | false;
  lines: QuoteLine[];
};
export type ValidateResult = { ok: true; id: number; name: string; emailed: boolean; warning: string | null };

// --- Commandes (sale.order confirmées) ---
export type PendingOrder = {
  id: number;
  name: string;
  source: SaleSource;
  partner: string | null;
  amount_total: number;
  currency: string | null;
  date_order: string | false;
  created_at?: string;
  delivery_status: string | false;
  invoice_status: string;
  state: string;
  /** Commande de l'agent encore à valider, ou commande déjà confirmée. */
  groupe: 'a_valider' | 'confirmee';
};
/** L'onglet Commandes compte ses deux groupes ; la page est à cheval sur les deux. */
export type OrderGroupFilter = 'tout' | 'a_valider' | 'confirmee';
export type PendingOrders = Page<PendingOrder> & {
  groupes: { a_valider: number; confirmees: number; tout: number };
};
export type DeliverLine = { move_id: number; product: string | null; product_id: number | null; ordered: number; available: number };
export type OrderDetail = {
  id: number;
  /** Emails déjà partis pour cette commande, avec leur état de remise. */
  emails: SentEmail[];
  /** Origine de la vente : commande transcrite par l'agent, ou saisie à la main. */
  source: SaleSource;
  name: string;
  state: string;
  stage: number;
  delivery_status: string | false;
  invoice_status: string;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  invoice_address: QuoteAddress | null;
  shipping_address: QuoteAddress | null;
  same_address: boolean;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  currency: string | null;
  date_order: string | false;
  created_at?: string;
  client_order_ref: string | false;
  picking_name: string | null;
  deliver_lines: DeliverLine[];
  lines: QuoteLine[];
};
export type ValidateOrderResult = {
  ok: true;
  id: number;
  name: string;
  delivered: boolean;
  backorder: boolean;
  invoice_id: number | null;
  invoice_name: string | null;
  /** La commande était déjà facturée : seule la livraison a été enregistrée. */
  already_invoiced?: boolean;
  lot_limited?: string[];
};

// --- Factures (account.move out_invoice) ---
export type PendingInvoice = {
  id: number;
  name: string;
  partner: string | null;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  state: string;
  payment_state: string;
  invoice_date: string | false;
  created_at?: string;
  invoice_date_due: string | false;
  invoice_origin: string | false;
  overdue: boolean;
};
export type InvoiceDetail = {
  /** Bons de livraison de cette facture (via la commande) — aucun lien direct en Odoo. */
  pickings: { id: number; name: string; date: string | null }[];
  /** Emails déjà partis pour cette facture, avec leur état de remise. */
  emails: SentEmail[];
  id: number;
  name: string;
  state: string;
  payment_state: string;
  overdue: boolean;
  stage: number;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  invoice_date: string | false;
  created_at?: string;
  invoice_date_due: string | false;
  invoice_origin: string | false;
  client_ref: string;
  lines: QuoteLine[];
};
// --- Suivi unifié (une "affaire" par sale.order) ---
export type DealRow = {
  id: number;
  name: string;
  source: SaleSource;
  partner: string | null;
  amount_total: number;
  currency: string | null;
  stage: number;
  next_label: string;
  /** Facture de la vente, dès qu'elle existe — c'est le document de l'étape en cours. */
  invoice: { id: number; name: string } | null;
  overdue: boolean;
  late: boolean;
};
export type DealFilter = 'en_cours' | 'tout' | 'terminees';
export type OrigineFilter = 'tout' | SaleSource;
export type DealCounts = {
  tout: number; en_cours: number; terminees: number;
  origine_tout: number; agent: number; manuel: number;
  capped: boolean;
};
export type Pipeline = CountedPage<DealRow, DealCounts>;
export type DealLine = {
  id: number;
  name: string;
  qty: number;
  price_unit: number;
  subtotal: number;
  product: string | null;
  product_id: number | null;
  available: number;
  shortage: boolean;
};
export type DealNext = {
  kind: 'validate_quote' | 'validate_order' | 'post_invoice' | 'invoice_actions' | 'done';
  label: string;
};
export type DealInvoice = {
  id: number;
  name: string;
  state: string;
  payment_state: string;
  amount_residual: number;
  invoice_date_due: string | false;
  overdue: boolean;
  web_url: string;
};
export type DealDetail = {
  /** Emails déjà partis pour cette vente, avec leur état de remise. */
  emails: SentEmail[];
  id: number;
  name: string;
  order_state: string;
  source: SaleSource;
  stage: number;
  next: DealNext;
  late: boolean;
  expected_date: string | false;
  stock_shortage: boolean;
  doc_key: string;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  invoice_address: QuoteAddress | null;
  shipping_address: QuoteAddress | null;
  same_address: boolean;
  date_order: string | false;
  created_at?: string;
  client_order_ref: string | false;
  delivery_status: string | false;
  invoice_status: string;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  currency: string | null;
  invoice: DealInvoice | null;
  lines: DealLine[];
};

export type PostInvoiceResult = { ok: true; id: number; name: string | null; state: string };
export type SendInvoiceResult = { ok: true; id: number; emailed: boolean };
export type PayInvoiceResult = { ok: true; id: number; payment_state: string };

export type LogEntry = {
  id: number;
  created_at: string;
  source: string | null;
  level: 'error' | 'warning' | 'info';
  workflow: string | null;
  node: string | null;
  title: string | null;
  message: string | null;
  context: unknown | null;
  execution_url: string | null;
  resolved: boolean;
};
export type LogsResponse = Page<LogEntry> & { openCount: number };

export type ControlSummary = {
  devisAValider: number;
  commandesATraiter: number;
  commandesEnRetard: number;
  facturesAValider: number;
  facturesImpayees: number;
  achatsEnRetard: number;
  stockRuptures: number;
  /** Entrées du Journal « à traiter » (non résolues, hors info) — même compteur que l'écran. */
  journalAlertes: number;
};

/** Période d'analyse des tableaux de bord (dates incluses, « YYYY-MM-DD »). */
export type DashRange = { from: string; to: string };

export type DashboardSummary = {
  currency: string;
  range: DashRange;
  /** Lu sur les factures comptabilisées, avoirs déduits — pas sur les commandes. */
  sales: {
    revenue: number;
    revenueTaxed: number;
    invoices: number;
    refunds: number;
    avgBasket: number;
  };
  pendingQuotes: number;
  receivables: { total: number; count: number };
  criticalProducts: number;
};
export type SalesReport = {
  currency: string;
  range: DashRange;
  granularity: 'day' | 'month';
  /** `bucket` = « YYYY-MM » au mois, « YYYY-MM-DD » au jour. */
  series: { bucket: string; revenue: number }[];
  topProducts: { id: number | null; name: string; revenue: number; qty: number }[];
  topCustomers: { name: string; revenue: number }[];
  /** `coverage` = part du CA dont le produit a un coût renseigné dans Odoo. */
  margin: { total: number; percent: number; coverage: number };
};
export type StockReport = {
  belowMin: { id: number | null; name: string; onHand: number; min: number; forecast: number; sold: number }[];
  outOfStock: { id: number | null; name: string; onHand: number; forecast: number; sold: number }[];
  outOfStockCount: number;
};

export type StockStatus = 'rupture' | 'below_min' | 'overstock' | 'ok';
export type StockItem = {
  id: number;
  name: string;
  code: string;
  on_hand: number;
  reserved: number;
  free: number;
  incoming: number;
  forecast: number;
  min: number;
  max: number;
  status: StockStatus;
  /** Quantité vendue (commandes confirmées) — clé de tri principale. */
  sold: number;
  /** Mode de suivi Odoo : `serial` = Hardware, `lot` = consommable. */
  tracking: string;
  use_expiration_date: boolean;
  /** Lots (ou n° de série) réellement en stock, et péremption la plus proche. */
  lots_count?: number;
  expiration?: string | null;
  expired?: boolean;
  expiring?: boolean;
};
export type StockCounts = {
  total: number; capped: boolean; rupture: number; below_min: number;
  overstock: number; reserved: number; in_stock: number;
  /** Nombre de produits de chaque onglet, recherche et statut appliqués. */
  tabs: { consommables: number; hardware: number };
};
export type StockProducts = CountedPage<StockItem, StockCounts>;

export type ProductLot = {
  id: number; name: string; qty: number; reserved: number;
  expiration: string | null; expired: boolean;
};
export type ProductDetail = {
  product: {
    id: number; name: string; code: string; barcode: string | null;
    category: string | null; uom: string | null; tracking: string;
    use_expiration_date: boolean; expiration_time: number;
    sale_price: number; cost_price: number;
    on_hand: number; reserved: number; free: number; incoming: number; forecast: number;
    min: number; max: number; status: StockStatus; active: boolean;
  };
  lots: ProductLot[];
  stock: { location: string; lot: string | null; qty: number; reserved: number }[];
  movements: {
    date: string | null; qty: number; lot: string | null; lot_id: number | null;
    sens: 'in' | 'out' | 'interne'; picking: string | null; picking_id: number | null;
    partner: string | null; origin: string | null; origin_link: DocLink | null;
  }[];
};

// --- Traçabilité par lot (rappel produit) ---
export type LotRow = {
  id: number;
  name: string;
  product: { id: number; name: string } | null;
  expiration: string | null;
  qty: number;
  created: string | null;
  expired: boolean;
  expiring: boolean;
};
export type LotCounts = { total: number; capped: boolean; en_stock: number; perime: number; bientot: number };
export type LotsList = CountedPage<LotRow, LotCounts>;
export type LotPartner = {
  id: number; name: string; email: string | null; phone: string | null;
  city: string | null; country: string | null;
};
export type LotMovement = {
  qty: number; date: string | null; picking: string | null; picking_id: number | null;
  origin: string | null; origin_link: DocLink | null;
  partner: LotPartner | null; reference: string | null;
};
export type LotTrace = {
  lot: {
    id: number; name: string; product: { id: number; name: string } | null;
    expiration: string | null; removal: string | null; qty: number;
    created: string | null; expired: boolean; ref: string | null;
  };
  receptions: LotMovement[];
  livraisons: LotMovement[];
  stock: { location: string; qty: number; reserved: number }[];
  clients: (LotPartner & { qty: number; dates: string[] })[];
};

// --- Achats : factures fournisseurs (account.move in_invoice) ---
export type ReceiptState = 'received' | 'partial' | 'service' | 'missing' | 'refund';
export type PurchaseRow = {
  id: number;
  name: string;
  ref: string;
  partner: string | null;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  state: string;
  payment_state: string;
  invoice_date: string | false;
  created_at?: string;
  invoice_origin: string | false;
  move_type: string;
  receipt: ReceiptState;
};
export type PurchaseFilter = 'all' | 'missing';
export type PurchaseCounts = { total: number; missing: number; capped: boolean };
export type PurchasesPage = CountedPage<PurchaseRow, PurchaseCounts>;
export type PurchaseLine = {
  id: number;
  name: string;
  product: string | null;
  product_id: number | null;
  qty: number;
  price_unit: number;
  subtotal: number;
};
export type PurchaseReception = { id: number; name: string; state: string };
export type PurchaseDetail = {
  id: number;
  name: string;
  ref: string;
  move_type: string;
  state: string;
  payment_state: string;
  receipt: ReceiptState;
  web_url: string;
  partner: string | null;
  invoice_date: string | false;
  created_at?: string;
  invoice_date_due: string | false;
  invoice_origin: string | false;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  receptions: PurchaseReception[];
  lines: PurchaseLine[];
};

// --- Clients et listes de prix (ticket #26) ---
export type PricelistRef = { id: number; name: string };
export type PricelistInfo = {
  id: number; name: string; currency: string; clients: number;
  /** Partagée = sert à zéro ou plusieurs clients : on refuse d'y poser une remise. */
  shared: boolean;
};
export type ClientRow = {
  id: number; name: string; city: string; country: string;
  email: string; phone: string; lang: string;
  pricelist: PricelistRef | null;
};
export type ClientsPage = CountedPage<ClientRow, { total: number; capped: boolean }> & {
  pricelists: PricelistInfo[];
};
/** Un geste métier, pas un enregistrement Odoo : remise, majoration ou prix imposé. */
export type PriceRuleRow = {
  id: number;
  scope: 'global' | 'produit';
  target: string | null;
  kind: 'remise' | 'majoration' | 'fixe' | 'autre';
  percent: number;
  price: number;
  /** Règle créée dans Odoo hors des gestes de l'app : affichée, pas évaluée. */
  unsupported: boolean;
};
export type ClientDetail = {
  client: {
    id: number; name: string; street: string; street2: string; zip: string; city: string;
    country: PricelistRef | null; phone: string; email: string; vat: string; lang: string;
  };
  pricelist: (PricelistRef & { currency: string; clients: number; shared: boolean }) | null;
  /** false = une majoration existe, le PDF masquera les prix catalogue partout. */
  shows_discounts: boolean;
  rules: PriceRuleRow[];
  pricelists: PricelistInfo[];
  langs: { code: string; name: string }[];
  countries: PricelistRef[];
};
export type CatalogueHit = { id: number; code: string; name: string; list_price: number };
/** Inventaire d'une liste de prix : à qui elle sert, ce qu'elle porte. */
export type PricelistAdmin = PricelistInfo & { rules: number; orders: number };
export type ClientPatch = Partial<{
  name: string; street: string; street2: string; zip: string; city: string;
  phone: string; email: string; vat: string; lang: string; country_id: number | null;
}>;
export type RulePayload = {
  scope: 'global' | 'produit';
  product_tmpl_id?: number;
  kind: 'remise' | 'majoration' | 'fixe';
  percent?: number;
  price?: number;
};

async function j<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status} ${body || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // Auth
  me: () => fetch('/api/auth/me', { credentials: 'include' }).then(j<Me>),
  login: (username: string, password: string) =>
    fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    }).then(j<Me>),
  logout: () => fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).then(j),

  // Conversations
  listConversations: (opts?: PageOpts & { q?: string }) =>
    fetch(`/api/conversations${pageParams(opts, { q: opts?.q })}`, { credentials: 'include' }).then(j<ConversationsPage>),
  createConversation: () =>
    fetch('/api/conversations', { method: 'POST', credentials: 'include' }).then(j<Conversation>),
  // Find-or-create the conversation tied to an Odoo document (e.g. "sale.order:195").
  openDocConversation: (docKey: string, title?: string) =>
    fetch('/api/conversations/for-doc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ docKey, title }),
    }).then(j<{ conversation: Conversation; existing: boolean }>),
  renameConversation: (id: number, title: string) =>
    fetch(`/api/conversations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title }),
    }).then(j),
  deleteConversation: (id: number) =>
    fetch(`/api/conversations/${id}`, { method: 'DELETE', credentials: 'include' }).then(j),
  listMessages: (id: number) =>
    fetch(`/api/conversations/${id}/messages`, { credentials: 'include' }).then(j<Message[]>),
  uploadAttachments: (convId: number, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('file', f, f.name);
    return fetch(`/api/conversations/${convId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    }).then(j<{ attachments: { name: string; size: number }[] }>);
  },

  // Admin
  listUsers: () => fetch('/api/admin/users', { credentials: 'include' }).then(j<AdminUser[]>),
  getEnvironment: () =>
    fetch('/api/admin/environment', { credentials: 'include' }).then(j<Environment>),
  createUser: (username: string, password: string, is_admin: boolean) =>
    fetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password, is_admin }),
    }).then(j),
  resetPassword: (id: number, password: string) =>
    fetch(`/api/admin/users/${id}/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    }).then(j),
  deleteUser: (id: number) =>
    fetch(`/api/admin/users/${id}`, { method: 'DELETE', credentials: 'include' }).then(j),

  // Devis (Odoo)
  listPendingQuotes: (opts?: PageOpts & SortOpts & { origine?: OrigineFilter }) =>
    fetch(`/api/quotes/pending${pageParams(opts, {
      ...sortParams(opts),
      origine: opts?.origine !== 'tout' ? opts?.origine : undefined,
    })}`, { credentials: 'include' }).then(j<Page<PendingQuote>>),
  getQuote: (id: number) => fetch(`/api/quotes/${id}`, { credentials: 'include' }).then(j<QuoteDetail>),
  /** `send: false` confirme la commande sans prévenir le client — l'envoi se fait à part. */
  validateQuote: (id: number, send = true) =>
    fetch(`/api/quotes/${id}/validate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ send }),
    }).then(j<ValidateResult>),

  sendOrderConfirmation: (id: number) =>
    fetch(`/api/quotes/${id}/send-confirmation`, { method: 'POST', credentials: 'include' })
      .then(j<{ ok: true; id: number; name: string; email: string }>),

  cancelQuote: (id: number) =>
    fetch(`/api/quotes/${id}/cancel`, { method: 'POST', credentials: 'include' })
      .then(j<{ ok: true; id: number; name: string; state: string }>),

  // Commandes (Odoo)
  listPendingOrders: (opts?: PageOpts & SortOpts & {
    livraison?: OrderDeliveryFilter; facturation?: OrderInvoiceFilter; groupe?: OrderGroupFilter;
  }) =>
    fetch(`/api/orders/pending${pageParams(opts, {
      ...sortParams(opts),
      livraison: opts?.livraison !== 'tout' ? opts?.livraison : undefined,
      facturation: opts?.facturation !== 'tout' ? opts?.facturation : undefined,
      groupe: opts?.groupe !== 'tout' ? opts?.groupe : undefined,
    })}`, { credentials: 'include' }).then(j<PendingOrders>),
  getOrder: (id: number) => fetch(`/api/orders/${id}`, { credentials: 'include' }).then(j<OrderDetail>),
  validateOrder: (id: number, deliveries?: { move_id: number; qty: number }[]) =>
    fetch(`/api/orders/${id}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(deliveries ? { deliveries } : {}),
    }).then(j<ValidateOrderResult>),

  // Factures (Odoo)
  listInvoices: (opts?: PageOpts & SortOpts & { etat?: InvoiceStateFilter; paiement?: InvoicePayFilter }) =>
    fetch(`/api/invoices/pending${pageParams(opts, {
      ...sortParams(opts),
      etat: opts?.etat !== 'tout' ? opts?.etat : undefined,
      paiement: opts?.paiement !== 'tout' ? opts?.paiement : undefined,
    })}`, { credentials: 'include' }).then(j<Page<PendingInvoice>>),
  getInvoice: (id: number) => fetch(`/api/invoices/${id}`, { credentials: 'include' }).then(j<InvoiceDetail>),
  postInvoice: (id: number) =>
    fetch(`/api/invoices/${id}/post`, { method: 'POST', credentials: 'include' }).then(j<PostInvoiceResult>),
  sendInvoice: (id: number) =>
    fetch(`/api/invoices/${id}/send`, { method: 'POST', credentials: 'include' }).then(j<SendInvoiceResult>),
  payInvoice: (id: number) =>
    fetch(`/api/invoices/${id}/pay`, { method: 'POST', credentials: 'include' }).then(j<PayInvoiceResult>),

  /** Annule un encaissement enregistré par erreur : la facture redevient « non payée ». */
  unpayInvoice: (id: number) =>
    fetch(`/api/invoices/${id}/unpay`, { method: 'POST', credentials: 'include' })
      .then(j<{ ok: true; id: number; payment_state: string; amount_residual: number;
                paiements: { name: string; amount: number }[] }>),

  // Suivi unifié (Odoo)
  getPipeline: (opts?: PageOpts & { filter?: DealFilter; origine?: OrigineFilter }) =>
    fetch(`/api/sales/pipeline${pageParams(opts, { filter: opts?.filter, origine: opts?.origine })}`,
      { credentials: 'include' }).then(j<Pipeline>),
  getDeal: (id: number) => fetch(`/api/sales/${id}`, { credentials: 'include' }).then(j<DealDetail>),

  // Centre de contrôle
  getControlSummary: () => fetch('/api/control/summary', { credentials: 'include' }).then(j<ControlSummary>),

  // Journal technique
  getLogs: (opts?: PageOpts & { scope?: string; level?: string }) =>
    fetch(`/api/logs${pageParams(opts, { scope: opts?.scope, level: opts?.level })}`,
      { credentials: 'include' }).then(j<LogsResponse>),
  resolveLog: (id: number, resolved: boolean) =>
    fetch(`/api/logs/${id}/resolve`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ resolved }),
    }).then(j<{ ok: true; id: number; resolved: boolean }>),

  // Dashboards (Odoo aggregates)
  getDashboardSummary: (r?: DashRange) =>
    fetch(`/api/dashboards/summary${rangeParams(r)}`, { credentials: 'include' }).then(j<DashboardSummary>),
  getSalesReport: (r?: DashRange) =>
    fetch(`/api/dashboards/sales${rangeParams(r)}`, { credentials: 'include' }).then(j<SalesReport>),
  getStockReport: () => fetch('/api/dashboards/stock', { credentials: 'include' }).then(j<StockReport>),

  // Achats (Odoo)
  listPurchases: (opts?: PageOpts & SortOpts & { filter?: PurchaseFilter; etat?: InvoiceStateFilter; paiement?: PurchasePayFilter }) =>
    fetch(`/api/purchases/pending${pageParams(opts, {
      ...sortParams(opts),
      filter: opts?.filter === 'missing' ? 'missing' : undefined,
      etat: opts?.etat !== 'tout' ? opts?.etat : undefined,
      paiement: opts?.paiement !== 'tout' ? opts?.paiement : undefined,
    })}`, { credentials: 'include' }).then(j<PurchasesPage>),
  getPurchase: (id: number) => fetch(`/api/purchases/${id}`, { credentials: 'include' }).then(j<PurchaseDetail>),
  postPurchase: (id: number) =>
    fetch(`/api/purchases/${id}/post`, { method: 'POST', credentials: 'include' })
      .then(j<{ ok: true; id: number; name: string | null; state: string; payment_state: string | null }>),

  // Écran Stocks (opérationnel)
  getStockProducts: (opts?: PageOpts & { q?: string; status?: string; tab?: string }) =>
    fetch(`/api/stock/products${pageParams(opts, {
      q: opts?.q,
      status: opts?.status && opts.status !== 'all' ? opts.status : undefined,
      tab: opts?.tab,
    })}`, { credentials: 'include' }).then(j<StockProducts>),

  getProduct: (id: number) =>
    fetch(`/api/stock/products/${id}`, { credentials: 'include' }).then(j<ProductDetail>),

  getLots: (opts?: PageOpts & { q?: string; filter?: string }) =>
    fetch(`/api/lots${pageParams(opts, {
      q: opts?.q,
      filter: opts?.filter && opts.filter !== 'all' ? opts.filter : undefined,
    })}`, { credentials: 'include' }).then(j<LotsList>),

  // Clients et listes de prix
  getClients: (opts?: PageOpts & { q?: string; sort?: string; dir?: string; liste?: string }) =>
    fetch(`/api/clients${pageParams(opts, {
      q: opts?.q, sort: opts?.sort, dir: opts?.dir,
      liste: opts?.liste && opts.liste !== 'tout' ? opts.liste : undefined,
    })}`, { credentials: 'include' }).then(j<ClientsPage>),

  getClient: (id: number) =>
    fetch(`/api/clients/${id}`, { credentials: 'include' }).then(j<ClientDetail>),

  updateClient: (id: number, patch: ClientPatch) =>
    fetch(`/api/clients/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(patch),
    }).then(j<{ ok: true; updated: string[] }>),

  setClientPricelist: (id: number, body: { pricelist_id?: number; create?: boolean }) =>
    fetch(`/api/clients/${id}/pricelist`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(body),
    }).then(j<{ ok: true; pricelist_id: number }>),

  addPriceRule: (id: number, body: RulePayload) =>
    fetch(`/api/clients/${id}/rules`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(body),
    }).then(j<{ ok: true; id: number }>),

  updatePriceRule: (id: number, ruleId: number, body: RulePayload) =>
    fetch(`/api/clients/${id}/rules/${ruleId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(body),
    }).then(j<{ ok: true }>),

  deletePriceRule: (id: number, ruleId: number) =>
    fetch(`/api/clients/${id}/rules/${ruleId}`, { method: 'DELETE', credentials: 'include' }).then(j<{ ok: true }>),

  getPricelists: () =>
    fetch('/api/pricelists', { credentials: 'include' }).then(j<{ items: PricelistAdmin[] }>),

  deletePricelist: (id: number) =>
    fetch(`/api/pricelists/${id}`, { method: 'DELETE', credentials: 'include' })
      .then(j<{ ok: true; deleted?: boolean; archived?: boolean; orders?: number }>),

  searchCatalogue: (q: string) =>
    fetch(`/api/clients/catalogue/search?q=${encodeURIComponent(q)}`, { credentials: 'include' })
      .then(j<{ items: CatalogueHit[] }>),

  getLot: (id: number) =>
    fetch(`/api/lots/${id}`, { credentials: 'include' }).then(j<LotTrace>),
};

/**
 * Télécharge un PDF servi par l'API. Un simple `<a href>` remplacerait l'écran par
 * le JSON d'erreur quand le rendu échoue (mot de passe Odoo absent, document sans
 * rapport) : ici l'erreur revient comme message et l'écran reste en place.
 */
export async function downloadPdf(url: string): Promise<void> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let msg = body;
    try { msg = JSON.parse(body).error ?? body; } catch { /* corps non JSON */ }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? 'document.pdf';
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  URL.revokeObjectURL(href);
}

export type StreamEvent =
  | { kind: 'session'; sessionId: string }
  | { kind: 'text'; delta: string }
  | { kind: 'tool_use'; name: string; input: unknown }
  | { kind: 'tool_result'; ok: boolean }
  | { kind: 'done'; usage?: unknown }
  | { kind: 'error'; message: string };

export type SseEvent =
  | { event: 'session' | 'text' | 'tool_use' | 'tool_result' | 'done' | 'title' | 'error' | 'end'; data: any };

/**
 * POST a message and read the SSE stream. Calls `onEvent` per event,
 * resolves when the server sends `event: end` or the stream closes.
 */
export async function postMessageStream(
  convId: number,
  content: string,
  onEvent: (ev: SseEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(`/api/conversations/${convId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ content }),
    signal,
  });
  if (!res.ok || !res.body) {
    throw new Error(`HTTP ${res.status}`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const lines = chunk.split('\n');
      let evType: string = 'message';
      const dataLines: string[] = [];
      for (const line of lines) {
        if (line.startsWith('event: ')) evType = line.slice(7).trim();
        else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
      }
      if (dataLines.length === 0) continue;
      let data: any;
      try {
        data = JSON.parse(dataLines.join('\n'));
      } catch {
        continue;
      }
      onEvent({ event: evType as any, data });
    }
  }
}
